import React, { useState, useEffect, useRef } from 'react';
import './StartupSequence.css';
import logoWhite from '../img/logo-white.png';
import pressStartArrow from '../img/press_start_arrow.png';
import pressStartText from '../img/press_start_text.png';
import bootupVideo from '../video/bootup.mp4';
import bootupVideoMobile from '../video/bootup-mobile.mp4';

interface StartupSequenceProps {
  onComplete: () => void;
  onStartAudio: () => void;
  onStopAudio?: () => void;
  initialRoute?: string;
}

interface BootLine {
  text: string;
  delay: number; // Delay AFTER this line appears
}

const bootLines: BootLine[] = [
  { text: "Copyright © 2025, orbitOS", delay: 1000 }, // Wait 1s after copyright
  { text: "", delay: 50 },
  { text: "remember:", delay: 100 },
  { text: "THINK LESS, DO MORE", delay: 1000 }, // 1s apart with cursor blinking
  { text: "DONE > GOOD > PERFECT", delay: 1000 }, // 1s apart with cursor blinking
  { text: "LOVE IS AN ORIENTATION OF SOUL", delay: 100 }, // Short delay after last statement
  { text: "", delay: 1000 }, // 1s pause AFTER all three statements
  { text: "INITIALIZING SYSTEM COMPONENTS...", delay: 200 },
  { text: "LOADING INTEGER BASIC INTO MEMORY...", delay: 200 },
  { text: "", delay: 50 },
  { text: "AVAILABLE SPACE.. 32 MB", delay: 200 },
  { text: "", delay: 50 },
  { text: "SYSTEM READY", delay: 100 }
];

const StartupSequence: React.FC<StartupSequenceProps> = ({ onComplete, onStartAudio, onStopAudio, initialRoute }) => {
  // Press Start state
  const [showPressStart, setShowPressStart] = useState(true);
  const [arrowVisible, setArrowVisible] = useState(true);

  // Bootup sequence state
  const [hasStarted, setHasStarted] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoRevealHeight, setVideoRevealHeight] = useState(0);
  const [showLoadingBar, setShowLoadingBar] = useState(false);
  const [showText, setShowText] = useState(false);
  const [displayedLines, setDisplayedLines] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [showTerminalCursor, setShowTerminalCursor] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const maxVisibleLines = 6; // Maximum lines visible in container

  // Detect if mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
  const videoSource = isMobile ? bootupVideoMobile : bootupVideo;

  // Preload video immediately on component mount (before press start is clicked)
  useEffect(() => {
    const video = document.createElement('video');
    video.src = videoSource;
    video.preload = 'auto';
    video.load();
    console.log('📹 Preloading video:', isMobile ? 'mobile' : 'desktop');
  }, [videoSource, isMobile]);

  // Load video when it becomes visible
  useEffect(() => {
    if (videoRef.current && showVideo) {
      videoRef.current.load();
    }
  }, [showVideo]);

  // Flash the arrow every 500ms on press start screen
  useEffect(() => {
    if (!showPressStart) return;

    const arrowInterval = setInterval(() => {
      setArrowVisible(prev => !prev);
    }, 500);

    return () => clearInterval(arrowInterval);
  }, [showPressStart]);

  // Terminal cursor blink
  useEffect(() => {
    if (!hasStarted) return;

    const cursorInterval = setInterval(() => {
      setShowTerminalCursor(prev => !prev);
    }, 530);

    return () => clearInterval(cursorInterval);
  }, [hasStarted]);

  // Handle user interaction to start
  const handleStart = () => {
    if (hasStarted) return;

    setShowPressStart(false);
    setHasStarted(true);

    // If /tiktok route, skip loading sequence entirely
    if (initialRoute === '/tiktok') {
      // Complete immediately
      onComplete();
      return;
    }

    // Start audio immediately (via parent component)
    onStartAudio();

    // Show video immediately and start playing
    setShowVideo(true);
    setVideoRevealHeight(0); // Start at 0%

    // Load and play video with better error handling
    setTimeout(() => {
      if (videoRef.current) {
        const video = videoRef.current;

        // Load the video first
        video.load();

        // Attempt to play, with fallback for mobile
        const playPromise = video.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              console.log("Video playing successfully");
            })
            .catch(err => {
              console.log("Video play error:", err);
              // Try again after a short delay (helps with mobile)
              setTimeout(() => {
                video.play().catch(e => console.log("Retry failed:", e));
              }, 300);
            });
        }
      }
    }, 100);

    // Video reveal steps (0% -> 25% -> 50% -> 75% -> 100%)
    setTimeout(() => setVideoRevealHeight(25), 750);   // 0.75s
    setTimeout(() => setVideoRevealHeight(50), 1500);  // 1.5s
    setTimeout(() => setVideoRevealHeight(75), 2250);  // 2.25s
    setTimeout(() => setVideoRevealHeight(100), 3000); // 3s

    // Show loading bar at 3s mark
    setTimeout(() => {
      setShowLoadingBar(true);
    }, 3000);

    // Show text at 3s mark
    setTimeout(() => {
      setShowText(true);
    }, 3000);

    /* TEMPORARY PRESAVE VERSION (saved for future use):
    window.open('https://distrokid.com/hyperfollow/adhivdhar/in-orbit', '_blank');
    */
  };

  useEffect(() => {
    if (showPressStart) {
      const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === ' ' || e.code === 'Space') {
          e.preventDefault();
          handleStart();
        }
      };

      const handleTouchStart = (e: TouchEvent) => {
        e.preventDefault();
        handleStart();
      };

      window.addEventListener('keydown', handleKeyPress);
      window.addEventListener('touchstart', handleTouchStart, { passive: false });

      return () => {
        window.removeEventListener('keydown', handleKeyPress);
        window.removeEventListener('touchstart', handleTouchStart);
      };
    }
  }, [showPressStart, hasStarted]);

  // Instant line rendering (no typing effect)
  useEffect(() => {
    if (!showText || currentLineIndex >= bootLines.length) return;

    const currentLine = bootLines[currentLineIndex];

    // Show line instantly and move to next after delay
    const lineTimer = setTimeout(() => {
      setDisplayedLines(prev => {
        const newLines = [...prev, currentLine.text];
        // Keep only the last maxVisibleLines
        return newLines.slice(-maxVisibleLines);
      });
      setCurrentLineIndex(prev => prev + 1);
    }, currentLine.delay);

    return () => clearTimeout(lineTimer);
  }, [showText, currentLineIndex, maxVisibleLines]);

  // Loading bar animation (linear)
  useEffect(() => {
    if (!showLoadingBar) return;

    const totalDuration = 6000; // 9s total - 3s (starts at 3s mark)
    const blockCount = 40; // Number of blocks in loading bar
    const blockSize = 100 / blockCount;
    const updateInterval = 50;

    const startTime = Date.now();

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const linearProgress = Math.min((elapsed / totalDuration) * 100, 100);

      // Snap to block increments
      const snappedProgress = Math.floor(linearProgress / blockSize) * blockSize;
      setLoadingProgress(snappedProgress);

      if (linearProgress >= 100) {
        clearInterval(progressInterval);
        setLoadingProgress(100);
      }
    }, updateInterval);

    return () => clearInterval(progressInterval);
  }, [showLoadingBar]);

  // Complete after 9 seconds
  useEffect(() => {
    if (!hasStarted) return;

    // Hard cutoff at 9 seconds
    const cutoffTimer = setTimeout(() => {
      onComplete();
    }, 9000);

    return () => clearTimeout(cutoffTimer);
  }, [hasStarted, onComplete]);

  // Double-tap / double-click anywhere on the startup screen skips straight to the desktop.
  const lastTapRef = useRef(0);

  const skipToHome = () => {
    onStopAudio?.();
    onComplete();
  };

  const handleTapSkip = () => {
    const now = Date.now();
    if (now - lastTapRef.current < 350) {
      skipToHome();
    }
    lastTapRef.current = now;
  };

  return (
    <div
      className="startup-sequence"
      onDoubleClick={skipToHome}
      onTouchEnd={handleTapSkip}
    >
      <div className="ScreenFrame">
        <div className="screen-black-border">
          <div className="startup-screen">
            {/* Press Start Screen */}
            {showPressStart && (
              <div className="press-start-screen" onClick={handleStart}>
                <img src={logoWhite} alt="Logo" className="press-start-logo" />
                <div className="press-start-title">in orbit</div>
                <div className="press-start-copyright">by adhiv dhar</div>
                <div className="press-start-button">
                  <img
                    src={pressStartArrow}
                    alt="Arrow"
                    className="press-start-arrow"
                    style={{ opacity: arrowVisible ? 1 : 0 }}
                  />
                  <img src={pressStartText} alt="Press Start" className="press-start-text" />
                </div>
                {/* TEMPORARY PRESAVE VERSION (saved for future use):
                <div className="press-start-early-message">you're not supposed to be here yet<br/><br/>but since you already are...</div>
                <div className="press-start-button">
                  <img
                    src={pressStartArrow}
                    alt="Arrow"
                    className="press-start-arrow"
                    style={{ opacity: arrowVisible ? 1 : 0 }}
                  />
                  <div className="press-start-presave-text">TAP TO PRESAVE</div>
                </div>
                */}
              </div>
            )}

            {/* Bootup Sequence */}
            {hasStarted && (
              <div className="bootup-screen">
                {/* Video at top with reveal effect */}
                {showVideo && (
                  <div className="bootup-video-container">
                    <div
                      className="bootup-video-wrapper"
                      style={{
                        height: `${videoRevealHeight}%`,
                        overflow: 'hidden'
                      }}
                    >
                      <video
                        ref={videoRef}
                        src={videoSource}
                        className="bootup-video"
                        muted
                        playsInline
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        x5-video-player-type="h5"
                        x5-video-player-fullscreen="false"
                        preload="auto"
                        loop
                      />
                    </div>
                  </div>
                )}

                {/* Loading bar */}
                {showLoadingBar && (
                  <div className="loading-bar-container">
                    <div className="loading-bar-background">
                      <div
                        className="loading-bar-fill"
                        style={{ width: `${loadingProgress}%` }}
                      />
                    </div>
                  </div>
                )}

                {/* Terminal text */}
                {showText && (
                  <div className="boot-terminal-text">
                    {displayedLines.map((line, index) => {
                      const isLastLine = index === displayedLines.length - 1;
                      const isWaitingForNext = currentLineIndex < bootLines.length;
                      const showCursorOnThisLine = isLastLine && isWaitingForNext && line !== "";

                      return (
                        <div key={index} className="boot-text-line">
                          <span className="boot-text-content">{line}</span>
                          {showCursorOnThisLine && (
                            <span
                              className="terminal-cursor-block"
                              style={{ opacity: showTerminalCursor ? 1 : 0 }}
                            >
                              █
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartupSequence;